import { Segment, Table, Header } from 'semantic-ui-react';
import { useCallback } from 'react';
import { useRefreshOptOutStatus, useRequestQuoteOptIn, useRefreshQuotesStatus, useRefreshQuotes } from '@app/sdk';
import PayDownPlanQuotesOptOut from "./PayDownPlanQuotesOptOut";
import PayDownPlanQuotesOptIn from './PayDownPlanQuotesOptIn';
import ValueWithAdjustment from "./ValueWithAdjustment";

type PayDownPlanQuotesTableProps = {
    accountId: string;
    payDownPlanQuotes: PayDownPlanQuotesResponse;
}

export default function PayDownPlanQuotesTable(props: PayDownPlanQuotesTableProps) {
    const refreshOptOutStatus = useRefreshOptOutStatus();
    const refreshQuotesStatus = useRefreshQuotesStatus();
    const refreshQuotes = useRefreshQuotes();

    const { mutate: requestQuoteOptIn, isPending: isEditPending } = useRequestQuoteOptIn();

    const quoteOptIn = useCallback(async (quoteId: string) => {
        await requestQuoteOptIn({
            accountId: props.accountId,
            quoteId
        });
        setTimeout(() => {
            refreshOptOutStatus();
            refreshQuotesStatus();
            refreshQuotes();
        }, 2000);
    }, [props.accountId, requestQuoteOptIn]);

    return (
        <>
            <Segment>
                <Header as="h2">Quotes</Header>
                <PayDownPlanQuotesOptOut accountId={props.accountId} />
                <PayDownPlanQuotesOptIn accountId={props.accountId} />
                <Table celled>
                    <Table.Body>
                        <Table.Row>
                            <Table.Cell>Quote Id</Table.Cell>
                            <Table.Cell>Term Duration</Table.Cell>
                            <Table.Cell>Adjusted Term Duration</Table.Cell>
                            <Table.Cell>Created Date</Table.Cell>
                            <Table.Cell>Interest Rate</Table.Cell>
                            <Table.Cell>Original Paydown Balance</Table.Cell>
                            <Table.Cell>Paydown Instalment Amount</Table.Cell>
                            <Table.Cell>Total Interest Payable</Table.Cell>
                            <Table.Cell>Total Repayable Amount</Table.Cell>
                            <Table.Cell>PaymentShock Projected Refund</Table.Cell>
                            <Table.Cell>Paydown Plan Start Date</Table.Cell>
                            <Table.Cell>Opt In</Table.Cell>
                        </Table.Row>
                        {props.payDownPlanQuotes.quotes.map((payDownPlanQuote) => {
                            return (
                                <Table.Row>
                                    <Table.Cell>{payDownPlanQuote.id}
                                        {payDownPlanQuote.paymentShockDetails != null &&
                                            (<pre><strong>⚡ (payment shock applied)</strong></pre>)}
                                    </Table.Cell>
                                    <Table.Cell>{payDownPlanQuote.termDuration}</Table.Cell>
                                    <Table.Cell>{payDownPlanQuote.adjustedTermDuration}</Table.Cell>
                                    <Table.Cell>{payDownPlanQuote.createdDate}</Table.Cell>
                                    <Table.Cell>{payDownPlanQuote.interestRate}</Table.Cell>
                                    <Table.Cell>
                                        <ValueWithAdjustment
                                            originalValue={payDownPlanQuote.initialPaydownQuoteParameters?.paydownBalance.value}
                                            adjustedValue={payDownPlanQuote.originalPayDownBalance?.value}
                                            showAdjustedIfOriginalMissing />
                                    </Table.Cell>
                                    <Table.Cell>
                                        <ValueWithAdjustment
                                            originalValue={payDownPlanQuote.initialPaydownQuoteParameters?.instalmentAmount.value}
                                            adjustedValue={payDownPlanQuote.payDownInstalmentAmount?.value}
                                            showAdjustedIfOriginalMissing />
                                    </Table.Cell>
                                    <Table.Cell>
                                        <ValueWithAdjustment
                                            originalValue={payDownPlanQuote.initialPaydownQuoteParameters?.totalInterestPayable.value}
                                            adjustedValue={payDownPlanQuote.totalInterestPayable?.value}
                                            showAdjustedIfOriginalMissing />
                                    </Table.Cell>
                                    <Table.Cell>
                                        <ValueWithAdjustment
                                            originalValue={payDownPlanQuote.initialPaydownQuoteParameters?.totalRepayableAmount.value}
                                            adjustedValue={payDownPlanQuote.totalRepayableAmount?.value}
                                            showAdjustedIfOriginalMissing />
                                    </Table.Cell>
                                    <Table.Cell>{payDownPlanQuote.paymentShockDetails?.projectedRefundAmount.value}</Table.Cell>
                                    <Table.Cell>{payDownPlanQuote.payDownPlanStartDate}</Table.Cell>
                                    <Table.Cell>
                                        {!payDownPlanQuote.selected &&
                                            <button
                                                type="button"
                                                className="ui button blue"
                                                onClick={() => quoteOptIn(payDownPlanQuote.id)}
                                                disabled={isEditPending}>
                                                <span>Select quote</span>
                                            </button>
                                        }
                                        {payDownPlanQuote.selected &&
                                            <p>Selected</p>
                                        }
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table>
            </Segment>
        </>
    )
}
